import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  "Med Spa / Aesthetics",
  "Real Estate",
  "Home Services",
  "Dental",
  "Fitness / Gym",
  "Coaching / Agency",
  "E-commerce",
  "Auto / Dealership",
  "Other",
];

export default function AuditDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    full_name: "",
    business_name: "",
    email: "",
    industry: "",
    database_size: "",
    monthly_revenue: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.business_name || !form.industry || !form.database_size) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/audit`, {
        full_name: form.full_name,
        business_name: form.business_name,
        email: form.email,
        industry: form.industry,
        database_size: parseInt(form.database_size, 10) || 0,
        monthly_revenue: form.monthly_revenue ? parseFloat(form.monthly_revenue) : null,
        notes: form.notes || null,
      });
      setSuccess(true);
      toast.success("Audit request received. We’ll be in touch within 24h.");
    } catch (err) {
      toast.error("Something broke. Try again or email paul@pipelineforge.co.uk.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => {
        setSuccess(false);
        setForm({
          full_name: "", business_name: "", email: "", industry: "",
          database_size: "", monthly_revenue: "", notes: "",
        });
      }, 250);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="audit-dialog"
        className="max-w-xl bg-[#0a0a0a] border-white/10 text-white rounded-none p-0 max-h-[90vh] overflow-y-auto"
      >
        {success ? (
          <div className="p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-[#ccff00] mx-auto mb-6" strokeWidth={1.5} />
            <h3 className="font-display text-3xl uppercase mb-3">You’re in.</h3>
            <p className="text-white/60 max-w-sm mx-auto leading-relaxed">
              We’ll review your database snapshot and send a tailored revenue
              projection within 24 hours. No spam, no calls until you ask.
            </p>
            <button
              data-testid="audit-dialog-close"
              onClick={() => handleClose(false)}
              className="mt-8 h-11 px-6 bg-[#ccff00] text-black font-bold uppercase tracking-wider text-xs hover:bg-[#b3e600] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8">
            <DialogHeader>
              <div className="text-xs uppercase tracking-[0.25em] text-[#ccff00] font-semibold mb-3">
                / Free database audit
              </div>
              <DialogTitle className="font-display text-3xl uppercase">
                Find your hidden revenue.
              </DialogTitle>
              <DialogDescription className="text-white/60 pt-2">
                Tell us about your database. We’ll send a no-fluff revenue
                projection within 24 hours.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <Field label="Full name *">
                <Input
                  data-testid="audit-full-name"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  placeholder="Jane Smith"
                  className="bg-[#050505] border-white/15 rounded-none h-11 focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
              <Field label="Business name *">
                <Input
                  data-testid="audit-business-name"
                  value={form.business_name}
                  onChange={(e) => update("business_name", e.target.value)}
                  placeholder="Acme Co."
                  className="bg-[#050505] border-white/15 rounded-none h-11 focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
              <Field label="Email *">
                <Input
                  data-testid="audit-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@acme.co"
                  className="bg-[#050505] border-white/15 rounded-none h-11 focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
              <Field label="Industry *">
                <Select value={form.industry} onValueChange={(v) => update("industry", v)}>
                  <SelectTrigger
                    data-testid="audit-industry"
                    className="bg-[#050505] border-white/15 rounded-none h-11 focus:ring-[#ccff00] focus:ring-1"
                  >
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/15 rounded-none text-white">
                    {INDUSTRIES.map((i) => (
                      <SelectItem key={i} value={i} className="focus:bg-white/10 rounded-none">
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Database size (contacts) *">
                <Input
                  data-testid="audit-db-size"
                  type="number"
                  min="0"
                  value={form.database_size}
                  onChange={(e) => update("database_size", e.target.value)}
                  placeholder="5000"
                  className="bg-[#050505] border-white/15 rounded-none h-11 focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
              <Field label="Monthly revenue (optional)">
                <Input
                  data-testid="audit-monthly-revenue"
                  type="number"
                  min="0"
                  value={form.monthly_revenue}
                  onChange={(e) => update("monthly_revenue", e.target.value)}
                  placeholder="$"
                  className="bg-[#050505] border-white/15 rounded-none h-11 focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
            </div>

            <div className="mt-4">
              <Field label="Anything else we should know?">
                <Textarea
                  data-testid="audit-notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="What CRM are you on? What’s your goal? Skip if unsure."
                  rows={3}
                  className="bg-[#050505] border-white/15 rounded-none focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
            </div>

            <button
              type="submit"
              data-testid="audit-form-submit"
              disabled={loading}
              className="mt-6 w-full h-12 bg-[#ccff00] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#b3e600] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Sending…" : "Get my free audit →"}
            </button>
            <p className="mt-3 text-xs text-white/40 text-center">
              By submitting you agree to a 1-time follow-up email. That’s it.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-white/60">{label}</Label>
      {children}
    </div>
  );
}
